/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {AnalyticsIds} from '@ion/core/src/analytics/types';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType, GenericObject} from '@ion/core/src/types/entities';
import {AssetSearchableProperty} from '@ion/core/src/types/services';
import {timeDiff} from '@ion/core/src/utils/DateUtil';
import Accordion, {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import ViewMoreButton from '@ion/dls/src/components/buttons/ViewMoreButton';
import Divider from '@ion/dls/src/components/divider/Divider';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import {VisionAccordion as CurrentVisionAccordion} from '@ion/dls/src/components/vision/current/VisionAccordion';
import {normalizeVideoLink} from '@ion/dls/src/components/vision/helpers/NormalizeUtils';
import IonVision from '@ion/dls/src/components/vision/IonVision';
import VisionEventItem from '@ion/dls/src/components/vision/VisionEventItem';
import VisionFeaturedEvent from '@ion/dls/src/components/vision/VisionFeaturedEvent';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useSmartSearchDevice} from '@ion/query/src/hooks/useSmartSearchDevice';
import {useVisionInfiniteQuery} from '@ion/query/src/hooks/useVisionInfiniteQuery';
import {AssetDetailsSection, useRegisteredAccordion} from '../assetDetailsAccordionStore';

export interface VisionEventsProps {
  asset: CVSAssetType;
}

interface SubsequentEvents {
  events: any;
  hasMultipleEvents: boolean;
  isVideoAvailable: boolean;
  isPhone: boolean;
};

// Video time threshold to validate when the video is not available
const VIDEO_TIME_THRESHOLD = 6; // In months

export const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    wrapper : {
      height    : '100%',
      minHeight : 150,
      cursor    : 'initial',
      overflow  : 'auto'
    },
    accordion : {
      padding : '0 15px'
    },
    header : {
      ...dlsTypography('panelCards'),
      color         : dlsColor('black'),
      fontFamily    : 'Roboto',
      fontWeight    : '600',
      fontSize      : '14px',
      lineHeight    : '17px',
      textTransform : 'uppercase',
      minHeight     : 48
    },
    loader : {
      height : 48
    },
    ...getAccordionWrapperStyles()
  };
});

// Defines a utility to validate when a video is available.
const hasVideoAvailable = event => timeDiff(new Date(), event?.appmsgEventTime, 'months') < VIDEO_TIME_THRESHOLD;

// Defines a utility to return subsequent Events.
const getSubsequentEvents = ({events, hasMultipleEvents, isVideoAvailable, isPhone}: SubsequentEvents) => {
  if (hasMultipleEvents) {
    return isVideoAvailable || isPhone ? events.slice(1) : events;
  }
  return null;
};

/**
 * The VisionEventsAccordion component is a reusable component that displays the
 * latest Vision events of a specific asset. It leverages the Accordion component
 * to present the events in a collapsible format.
 *
 * To use the VisionEventsAccordion component, you can simply import it and pass the required props.
 *
 * ```typescript
 *
 * import {VisionEventsAccordion} from './VisionEventsAccordion';
 *
 * const SomeComponent = () => {
 *  return (
 *    <VisionEventsAccordion asset={asset} />
 *  );
 * }
 *
 * ```
 *
 */
export const VisionEventsAccordion = ({asset}: VisionEventsProps) => {
  const classes                                 = useStyles();
  const isPhone                                 = useIsPhone();
  const [iOnVisionURL, setIONVisionURL]         = useState(null);
  const [deviceIdList, setDeviceIdList]         = useState(null);
  const [eventData, setEventData]               = useState(null);
  const [showMobileEvents, setShowMobileEvents] = useState(false);
  const [events, setEvents]                     = useState(null);
  const hasMultipleEvents                       = events?.length > 1;
  const featuredEvent                           = hasMultipleEvents ? events[0] : null;

  const hasFirstEventVideo = useMemo(() =>
    featuredEvent ? hasVideoAvailable(featuredEvent) : false, [featuredEvent]);

  const subsequentEvents = useMemo(() => getSubsequentEvents({
    events,
    isPhone,
    hasMultipleEvents,
    isVideoAvailable : hasFirstEventVideo
  }), [hasFirstEventVideo, hasMultipleEvents, events, isPhone]);

  const videoNotAvailableLabel = translate('ionvision:videoNotAvailable');

  const {
    data,
    isLoading: isSmartSearchLoading,
    isSuccess: isSmartSearchSuccess
  }: GenericObject = useSmartSearchDevice({
    id                   : asset.id,
    isAssetId            : true,
    isReturnSingleDevice : false,
    isPulseUrl           : false
  });

  useEffect(() => {
    if (data?.length) {
      const ids = data?.map(device => device?.id)?.toString();
      setDeviceIdList(ids);
    }
  }, [data]);

  const {
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isSuccess          : isVisionSuccess,
    isLoading          : isVisionLoading,
    data               : visionEvents
  } = useVisionInfiniteQuery({
    deviceId   : deviceIdList,
    enabled    : !!deviceIdList?.length,
    sortOn     : [`-${AssetSearchableProperty.avlEventTime}`],
    maxResults : 6
  });

  useEffect(() => {
    if (visionEvents) {
      const flattenedEvents = visionEvents?.pages?.reduce((acc, page) => {
        return [...acc, ...page];
      }, []);
      setEvents(flattenedEvents);
    }
  }, [visionEvents]);

  function onIonVisionClick(event: any, isVideoAvailable: boolean = true) {
    if (isVideoAvailable) {
      const link = normalizeVideoLink(event);
      setIONVisionURL(link);
      setEventData(event);
    }
  };

  function getFeaturedEventLink(event) {
    return normalizeVideoLink(event);
  };

  const handleMobileViewMoreClick = useCallback(function() {
    if (!showMobileEvents && subsequentEvents?.length) {
      setShowMobileEvents(true);
    }
    if (showMobileEvents && hasNextPage) {
      fetchNextPage();
    }
  }, [
    showMobileEvents,
    subsequentEvents,
    fetchNextPage,
    hasNextPage
  ]);

  const showCurrentVision = isCurrentTheme() && isVisionSuccess && Boolean(events?.length);

  const {
    expanded : isVisionExpanded,
    onExpand : onVisionExpand
  } = useRegisteredAccordion(AssetDetailsSection.vision, false, showCurrentVision);

  if (isVisionSuccess && events?.length) {
    const visionDialog = iOnVisionURL && !isPhone && (
      <IonVision urls={[iOnVisionURL]}
        elementId={AnalyticsIds.visionAssetDetailsAccordion}
        selectedAsset={asset}
        setVideoOpen={setIONVisionURL}
        eventData={eventData}
        nativePlayerOnMobile
        showVideoPlayer
        audio
        videoOpen />
    );

    return isCurrentTheme() ? (
      <>
        <CurrentVisionAccordion title={translate('maps:popup.vision')}
          featuredEvent={hasFirstEventVideo ? featuredEvent : null}
          videoSource={hasFirstEventVideo ? getFeaturedEventLink(featuredEvent) : null}
          onEventClick={function(event: GenericObject) {
            onIonVisionClick(event, hasVideoAvailable(event));
          }}
          audio
          historyEvents={
            hasFirstEventVideo
              ? ((!isPhone || showMobileEvents) ? subsequentEvents : [])
              : ((!isPhone || showMobileEvents) ? events : events.slice(0, 1))
          }
          isVideoAvailable={hasVideoAvailable}
          videoNotAvailableLabel={videoNotAvailableLabel}
          showShowMore={hasNextPage}
          showMoreLoading={isFetchingNextPage}
          showMoreLabel={translate(`common:actions.${isCurrentTheme() ? 'showMore' : 'viewMore'}`)}
          onShowMoreClick={isPhone ? handleMobileViewMoreClick : fetchNextPage}
          expanded={isVisionExpanded}
          onExpand={onVisionExpand}
          defaultExpanded />
        {visionDialog}
      </>
    ) : (
      <div className={classes.accordionWrapper}>
        <Accordion className={classes.accordion}
          header={
            <span className={classes.header}>
              {translate('maps:popup.vision')}
            </span>
          }
          details={
            <div className={classes.wrapper}>
              {hasFirstEventVideo ? (
                <VisionFeaturedEvent event={featuredEvent}
                  videoSource={getFeaturedEventLink(featuredEvent)}
                  eventClick={function() { onIonVisionClick(featuredEvent); }}
                  displayEventName />
              ) : isPhone && <VisionEventItem key={featuredEvent?.id}
                showEventBorder={false}
                eventClick={function() { onIonVisionClick(featuredEvent, hasFirstEventVideo); }}
                isPhone={isPhone}
                event={featuredEvent}
                hasVideo={hasFirstEventVideo}
                toolTipLabel={!hasFirstEventVideo ? videoNotAvailableLabel : null} />
              }
              {(!isPhone || showMobileEvents) && hasMultipleEvents && <Divider />}
              {(!isPhone || showMobileEvents) && subsequentEvents?.map?.((event: GenericObject, index: number) => {
                const hasVideo = hasVideoAvailable(event);
                return (
                  <VisionEventItem key={`${index}-${event?.id}`}
                    showEventBorder={index !== subsequentEvents.length - 1}
                    eventClick={function() { onIonVisionClick(event, hasVideo); }}
                    isPhone={isPhone}
                    event={event}
                    hasVideo={hasVideo}
                    toolTipLabel={!hasVideo ? videoNotAvailableLabel : null} />
                );
              })}
              {hasNextPage && (
                <ViewMoreButton loading={isFetchingNextPage || isFetching}
                  handleLoadMore={isPhone ? handleMobileViewMoreClick : fetchNextPage}
                  fullWidth={!isPhone} />
              )}
              {visionDialog}
            </div>
          }
          borderBottom={'none'}
          padding={'0px'}
          isCondensedVariant
          defaultExpanded />
      </div>
    );
  }

  if (events?.length === 0 && isVisionSuccess) {
    return null;
  }
  if (isSmartSearchSuccess && isNil(deviceIdList)) {
    return null;
  }

  return (
    <div className={classes.loader}>
      <TombstoneLoader />
    </div>
  );
};

export default VisionEventsAccordion;
